#!/usr/bin/env node
/**
 * FutureX CLI entrypoint (Phase 0).
 *
 *   futurex                 open the interactive REPL
 *   futurex ask "<prompt>"  one-shot: stream a single answer
 *   futurex help            show usage
 *
 * Dependency-free arg parsing (commander arrives when the command surface grows).
 */
import type { EffortLevel, Tier } from '@fim/futurex-protocol';
import { normalizeTier, resolveTier, TIER_IDS } from '@fim/futurex-protocol';
import { runAsk } from './ask.js';
import { runRepl } from './repl.js';
import { runLogin, runLogout, runWhoami } from './commands.js';
import { runBuild } from './build-command.js';
import { runIndex, runSymbols, runImports } from './index-command.js';
import { runInit, runCompact } from './memory-command.js';
import { runPermissions } from './permissions-command.js';
import { findRepoRoot } from './repo.js';
import { runPlan, runResume, type PlanFlags } from './plan-command.js';
import { type ValueTier } from './planning-config.js';
import { runSkills } from './skills-command.js';
import { runVerify } from './verify-command.js';
import { runAgent } from './agent.js';
import { ensureLoggedIn } from './device.js';
import type { CliOptions } from './options.js';
import { color, error, info, line } from './ui.js';

const VERSION = '0.3.0';

interface ParsedArgs {
  command: string;
  positionals: string[];
  flags: Record<string, string | boolean>;
}

/** Flags that take a value (`--model x` / `--model=x`). */
const VALUE_FLAGS = new Set([
  'model',
  'max-tokens',
  'email',
  'password',
  'top-k',
  'max-steps',
  'tier',
  'effort',
  'value',
  'budget',
]);
const ALIASES: Record<string, string> = { r: 'reason', h: 'help', v: 'version', m: 'model' };

function parseArgs(argv: readonly string[]): ParsedArgs {
  let command = '';
  const positionals: string[] = [];
  const flags: Record<string, string | boolean> = {};
  let noMoreFlags = false;

  const takePositional = (tok: string): void => {
    if (command === '' && positionals.length === 0) command = tok;
    else positionals.push(tok);
  };

  for (let i = 0; i < argv.length; i++) {
    const tok = argv[i];
    if (tok === undefined) continue;

    if (noMoreFlags) {
      takePositional(tok);
      continue;
    }
    if (tok === '--') {
      // Everything after `--` is positional (e.g. a prompt that begins with `-`).
      noMoreFlags = true;
      continue;
    }

    if (tok.startsWith('--')) {
      let body = tok.slice(2);
      let value: string | undefined;
      const eq = body.indexOf('=');
      if (eq !== -1) {
        value = body.slice(eq + 1);
        body = body.slice(0, eq);
      }
      if (VALUE_FLAGS.has(body)) {
        if (value === undefined) {
          const next = argv[i + 1];
          if (next !== undefined && !next.startsWith('-')) {
            value = next;
            i++;
          }
        }
        // Record an empty value when none could be consumed, so downstream
        // validation rejects `--budget` / `--budget -5` instead of silently
        // dropping the user's flag.
        flags[body] = value ?? '';
      } else {
        flags[body] = value === undefined ? true : value !== 'false';
      }
    } else if (tok.startsWith('-') && tok !== '-') {
      const name = ALIASES[tok.slice(1)] ?? tok.slice(1);
      if (VALUE_FLAGS.has(name)) {
        const next = argv[i + 1];
        if (next !== undefined && !next.startsWith('-')) {
          flags[name] = next;
          i++;
        } else {
          flags[name] = '';
        }
      } else {
        flags[name] = true;
      }
    } else {
      takePositional(tok);
    }
  }

  return { command, positionals, flags };
}

/** Parse the named effort (`--effort off|high|max`, falling back to `--max`/`--reason`). */
function effortFromFlags(flags: Record<string, string | boolean>): EffortLevel | undefined {
  const raw = typeof flags.effort === 'string' ? flags.effort.trim().toLowerCase() : '';
  if (raw !== '') {
    if (raw === 'off' || raw === 'high' || raw === 'max') return raw;
    throw new Error(`invalid --effort "${raw}" (expected: off | high | max)`);
  }
  if (flags.max === true) return 'max';
  if (flags.reason === true) return 'high';
  return undefined;
}

function buildOptions(flags: Record<string, string | boolean>): CliOptions {
  // Treat an empty `--model=` as unset so we fall back to a real default rather
  // than sending `model: ''` (which the provider would forward into a 400).
  const explicitModel =
    typeof flags.model === 'string' && flags.model.trim() !== '' ? flags.model.trim() : undefined;

  const effort = effortFromFlags(flags);

  let maxTokens: number | undefined;
  if (typeof flags['max-tokens'] === 'string') {
    const n = Number(flags['max-tokens']);
    if (Number.isFinite(n) && n > 0) maxTokens = Math.floor(n);
    else info(`ignoring invalid --max-tokens "${flags['max-tokens']}"`);
  }

  // The user-facing unit is a MODULE (fx-*). Default fx-fast; --pro / --max pick a
  // stronger module; --tier <id> is explicit. resolveTier ENFORCES allowed effort.
  // An explicit --model is a power-user escape hatch over the module's model.
  let tier: Tier;
  const tierRaw = typeof flags.tier === 'string' ? flags.tier.trim() : '';
  if (tierRaw !== '') {
    const norm = normalizeTier(tierRaw);
    if (!norm) throw new Error(`unknown module "${tierRaw}" (expected: ${TIER_IDS.join(' | ')})`);
    tier = norm;
  } else if (flags.pro === true || effort === 'max') {
    tier = 'fx-pro';
  } else {
    tier = 'fx-fast';
  }
  const resolved = resolveTier(tier, { effort, maxTokens });

  let topK = 6;
  if (typeof flags['top-k'] === 'string') {
    const n = Number(flags['top-k']);
    if (Number.isFinite(n) && n >= 0) topK = Math.floor(n);
    else info(`ignoring invalid --top-k "${flags['top-k']}"`);
  }

  return {
    model: explicitModel ?? resolved.model,
    reasoning: resolved.reasoning,
    tier,
    effort: resolved.effort,
    maxTokens: resolved.maxTokens,
    useMemory: flags['no-memory'] !== true,
    useRepo: flags['no-repo'] !== true,
    useContext: flags['no-context'] !== true,
    topK,
  };
}

const VALUE_TIERS: readonly ValueTier[] = ['low', 'standard', 'high', 'critical'];

/** Parse the planning-layer flags (`--restart`, `--value <tier>`, `--budget <n>`). */
function planFlags(flags: Record<string, string | boolean>): PlanFlags {
  let valueTier: ValueTier | undefined;
  if (typeof flags.value === 'string') {
    const v = flags.value.trim().toLowerCase() as ValueTier;
    if (!VALUE_TIERS.includes(v)) {
      throw new Error(`invalid --value "${flags.value}" (expected: ${VALUE_TIERS.join(' | ')})`);
    }
    valueTier = v;
  }
  let budgetTokens: number | undefined;
  if (typeof flags.budget === 'string') {
    const n = Number(flags.budget);
    if (Number.isFinite(n) && n > 0) budgetTokens = Math.floor(n);
    else throw new Error(`invalid --budget "${flags.budget}" (expected a positive token count)`);
  }
  return {
    restart: flags.restart === true,
    ...(valueTier !== undefined ? { valueTier } : {}),
    ...(budgetTokens !== undefined ? { budgetTokens } : {}),
  };
}

function printHelp(): void {
  line(`${color.bold('futurex')} — terminal AI coding agent ${color.dim(`(v${VERSION})`)}`);
  line();
  line(color.bold('Usage'));
  line('  futurex                  open the interactive REPL');
  line('  futurex ask "<prompt>"   one-shot answer, then exit');
  line('  futurex agent "<task>"   agentic loop: the model uses tools (reads auto, writes gated)');
  line('  futurex index            index the repo for retrieval-augmented answers');
  line('  futurex symbols [name]   query the AST symbol index ("is <name> defined?")');
  line('  futurex imports [file]   show the import graph + broken imports');
  line('  futurex init             create futurex-memory.md (project memory) if absent');
  line('  futurex compact          bound futurex-memory.md size (regenerate + archive)');
  line('  futurex plan "<task>"    pre-build plan: goal + value/budget + research + tooling + task DAG');
  line('  futurex build "<task>"   plan, then execute against the plan (stops when the goal is met)');
  line('  futurex resume [id]      inspect/resume pipeline checkpoints');
  line('  futurex skills [request] list skills, or route a request to the matching skills');
  line('  futurex permissions      view/change per-project consent (show | set <kind> <state> | reset)');
  line('  futurex verify [--list]  run project diagnostics (typecheck/lint/test/build)');
  line('  futurex test             run the project test script');
  line('  futurex login            sign in via a browser link code (--no-browser to print only)');
  line('  futurex logout           clear stored credentials');
  line('  futurex whoami           show the current backend/auth mode');
  line('  futurex help             show this help');
  line();
  line(color.bold('Options'));
  line('      --tier <module>      fx-pro | fx-fast | fx-mini | fx-eco (the module preset)');
  line('      --effort <level>     off | high | max (enforced against the module)');
  line('  -r, --reason             use more reasoning effort (high)');
  line('      --max                use maximum reasoning (implies the fx-pro module)');
  line('      --pro                use the fx-pro module');
  line('  -m, --model <id>         override the underlying model id (advanced)');
  line('      --max-tokens <n>     cap output tokens');
  line('      --no-memory          ignore futurex-memory.md');
  line('      --no-repo            do not include the repo card');
  line('      --no-context         do not augment with retrieved repo chunks');
  line('      --top-k <n>          chunks to retrieve per turn (default 6)');
  line('      --auto-approve       (agent) run mutating tools without prompting (dangerous)');
  line('      --max-steps <n>      (agent) max model/tool rounds (default 32)');
  line('      --no-verify          (agent) skip the automatic typecheck/lint/test pass after edits');
  line('      --value <tier>       (plan/build) override the detected project value: low | standard | high | critical');
  line('      --budget <n>         (plan/build) cap the build token budget');
  line('      --restart            (plan/build) ignore checkpoints and re-plan from scratch');
  line(color.dim('  note: in a monorepo, `index` scans the whole workspace root.'));
  line(color.dim('  note: --tier/--effort apply to ask & chat; the agent executor runs fast by design.'));
  line('  -v, --version            print version');
  line();
  line(color.dim('Set FX_API_KEY in your .env (auto-loaded when you run `futurex`).'));
}

async function main(): Promise<void> {
  const { command, positionals, flags } = parseArgs(process.argv.slice(2));

  if (flags.version === true) {
    line(`futurex ${VERSION}`);
    return;
  }
  if (flags.help === true || command === 'help') {
    printHelp();
    return;
  }

  const opts = buildOptions(flags);

  // Login is mandatory for anything that reaches the gateway. These commands
  // sign in via the browser "link code" flow on first use (no-op when already
  // signed in); local utilities (index/symbols/init/verify/…) stay offline.
  const AI_COMMANDS = new Set(['', 'chat', 'ask', 'agent', 'build', 'plan']);
  if (AI_COMMANDS.has(command)) {
    try {
      await ensureLoggedIn();
    } catch (err) {
      error(err instanceof Error ? err.message : String(err));
      process.exitCode = 1;
      return;
    }
  }

  switch (command) {
    case '':
    case 'chat':
      await runRepl(opts);
      return;
    case 'ask':
      await runAsk(positionals.join(' '), opts);
      return;
    case 'index':
      await runIndex();
      return;
    case 'symbols':
      runSymbols(positionals.join(' '));
      return;
    case 'imports':
      runImports(positionals.join(' '));
      return;
    case 'init':
      runInit();
      return;
    case 'compact':
      runCompact();
      return;
    case 'plan':
      await runPlan(positionals.join(' '), opts, planFlags(flags));
      return;
    case 'build':
      await runBuild(positionals.join(' '), opts, planFlags(flags));
      return;
    case 'resume':
      runResume(positionals[0]);
      return;
    case 'skills':
      runSkills(positionals.join(' '));
      return;
    case 'verify':
      await runVerify({ list: flags.list === true });
      return;
    case 'test':
      await runVerify({ only: 'test' });
      return;
    case 'agent': {
      const maxRaw = typeof flags['max-steps'] === 'string' ? Number(flags['max-steps']) : NaN;
      const maxSteps = Number.isFinite(maxRaw) && maxRaw > 0 ? Math.floor(maxRaw) : 32;
      await runAgent(positionals.join(' '), opts, {
        autoApprove: flags['auto-approve'] === true,
        maxSteps,
        verify: flags['no-verify'] === true ? 'off' : 'auto',
      });
      return;
    }
    case 'permissions':
      runPermissions(findRepoRoot(), positionals.join(' '));
      return;
    case 'login':
      await runLogin(flags);
      return;
    case 'logout':
      runLogout();
      return;
    case 'whoami':
      runWhoami();
      return;
    default:
      error(`unknown command: ${color.bold(command)}`);
      line();
      line(`run ${color.cyan('futurex help')} for usage.`);
      process.exitCode = 1;
  }
}

main().catch((err: unknown) => {
  error(err instanceof Error ? err.message : String(err));
  process.exitCode = 1;
});
